import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { ROLE_LABELS } from '../../shared/layer-naming'
import {
  fileFromIssue,
  messageIdForCode,
  type FileIssue,
} from '../../shared/rejection-messages'
import type { FileProgress } from '../../shared/run-upload'
import {
  isValidSlug,
  planUpload,
  slugFromDisplayName,
} from '../../shared/upload-plan'
import FileRow from '../upload-dropzone/FileRow'
import UploadDropzone from '../upload-dropzone/UploadDropzone'

export interface OptionWizardProps {
  open: boolean
  sectionId: string
  uploading?: boolean
  progress?: Record<string, FileProgress>
  issues?: FileIssue[]
  onClose: () => void
  onTryOn?: (files: File[]) => void
  onSubmit: (input: {
    files: File[]
    displayName: string
    slug: string
    sortOrder?: number
  }) => void
}

const STEPS = ['files', 'check', 'name'] as const

export default function OptionWizard({
  open,
  sectionId,
  uploading = false,
  progress,
  issues,
  onClose,
  onTryOn,
  onSubmit,
}: OptionWizardProps) {
  const { formatMessage } = useIntl()
  const [step, setStep] = useState(0)
  const [files, setFiles] = useState<File[]>([])
  const [displayName, setDisplayName] = useState('')

  const plan = useMemo(() => planUpload(files.map((f) => f.name)), [files])
  const slug = slugFromDisplayName(displayName)
  const issuesFor = (name: string) =>
    (issues ?? [])
      .filter((i) => fileFromIssue(i) === name)
      .map((i) => i.detail ?? formatMessage({ id: messageIdForCode(i.code) }))
      .join(' ')
  const generalIssues = (issues ?? []).filter((i) => !fileFromIssue(i))

  const reset = () => {
    setStep(0)
    setFiles([])
    setDisplayName('')
  }

  const close = () => {
    if (uploading) {
      return
    }
    reset()
    onClose()
  }

  const problemFor = (name: string): string | undefined => {
    const server = issuesFor(name)
    if (server) {
      return server
    }
    const unreadable = plan.review.problems.find((p) => p.filename === name)
    if (unreadable) {
      return unreadable.detail
    }
    const layer = plan.review.layers.find((l) => l.filename === name)
    if (layer && plan.review.base && layer.base !== plan.review.base) {
      return formatMessage(
        { id: 'avatarStudio.reject.mixedOptionBase' },
        { file: name, base: plan.review.base },
      )
    }

    return undefined
  }

  const roleFor = (name: string): string | undefined => {
    const layer = plan.review.layers.find((l) => l.filename === name)

    return layer ? ROLE_LABELS[layer.role] : undefined
  }

  return (
    <Dialog open={open} onClose={close} fullWidth={true} maxWidth="sm">
      <DialogTitle>
        {formatMessage({ id: 'avatarStudio.wizard.title' }, { section: sectionId })}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((name) => (
            <Step key={name}>
              <StepLabel>
                {formatMessage({ id: `avatarStudio.wizard.step.${name}` })}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Stack spacing={2}>
            <UploadDropzone
              disabled={uploading}
              onFiles={(picked) => {
                setFiles(picked)
                setStep(1)
              }}
            />
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={1.5}>
            {generalIssues.length > 0 && (
              <Alert severity="error">
                {generalIssues
                  .map(
                    (i) =>
                      i.detail ?? formatMessage({ id: messageIdForCode(i.code) }),
                  )
                  .join(' ')}
              </Alert>
            )}
            {plan.review.missingBase && (
              <Alert severity="error">
                {formatMessage({ id: 'avatarStudio.reject.missingBaseLayer' })}
              </Alert>
            )}
            {files.map((f) => (
              <FileRow
                key={f.name}
                name={f.name}
                roleLabel={roleFor(f.name)}
                problem={problemFor(f.name)}
                progress={progress?.[f.name]}
              />
            ))}
            {plan.canUpload && onTryOn && (
              <Alert severity="info">
                {formatMessage({ id: 'avatarStudio.preview.tryOnHint' })}
              </Alert>
            )}
            {!plan.canUpload && (
              <Alert severity="warning">
                {formatMessage(
                  { id: 'avatarStudio.wizard.blocked' },
                  { count: plan.blocking },
                )}
              </Alert>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <TextField
              size="small"
              autoFocus={true}
              label={formatMessage({ id: 'avatarStudio.wizard.displayName' })}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              inputProps={{ maxLength: 120 }}
              disabled={uploading}
            />
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                {formatMessage({ id: 'avatarStudio.wizard.slug' })}
              </Typography>
              <Typography variant="body2" translate="no">
                {slug || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatMessage({ id: 'avatarStudio.wizard.slugPermanent' })}
              </Typography>
            </Stack>
            {generalIssues.length > 0 && (
              <Alert severity="error">
                {generalIssues
                  .map(
                    (i) =>
                      i.detail ?? formatMessage({ id: messageIdForCode(i.code) }),
                  )
                  .join(' ')}
              </Alert>
            )}
            {progress &&
              files.map((f) => (
                <FileRow key={f.name} name={f.name} progress={progress[f.name]} />
              ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} disabled={uploading}>
          {formatMessage({ id: 'avatarStudio.editor.cancel' })}
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep(step - 1)} disabled={uploading}>
            {formatMessage({ id: 'avatarStudio.wizard.back' })}
          </Button>
        )}
        {step === 1 && onTryOn && (
          <Button
            startIcon={<VisibilityOutlinedIcon />}
            disabled={!plan.canUpload}
            onClick={() => onTryOn(files)}
          >
            {formatMessage({ id: 'avatarStudio.preview.tryOn' })}
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            disabled={!plan.canUpload}
            onClick={() => setStep(2)}
          >
            {formatMessage({ id: 'avatarStudio.wizard.next' })}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            disabled={uploading || !isValidSlug(slug)}
            onClick={() => onSubmit({ files, displayName, slug })}
          >
            {formatMessage({
              id: uploading
                ? 'avatarStudio.wizard.uploading'
                : 'avatarStudio.wizard.submit',
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
